import React, { useState, useEffect } from 'react';
import { School, BookOpen, Users, Award, Sparkles, Star, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import ComparisonTable from '../../shared/ComparisonTable';
import SchoolCard from '../../shared/SchoolCard';
import { getCollegeName, getSchoolColor } from '../../shared/designSystem';
import { generateBasicData } from '../../utils/dataGeneration';

const AcademicsTab = ({ colleges = [], currentUser, portfolioLocked = false, studentProfile = {}, onDeleteCollege, onDataRefresh }) => {
  const [academicFitData, setAcademicFitData] = useState(null);
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false);
  const [generatingDataForCollege, setGeneratingDataForCollege] = useState(null);

  // Load cached academic fit data on mount
  useEffect(() => {
    const loadCachedData = () => {
      const cached = {};

      colleges.forEach(college => {
        const name = getCollegeName(college);
        // Check if academic_fit_analysis exists and is not an empty object
        if (college.academic_fit_analysis &&
            typeof college.academic_fit_analysis === 'object' &&
            Object.keys(college.academic_fit_analysis).length > 0) {
          cached[name] = college.academic_fit_analysis;
          console.log(`✅ [AcademicsTab] Loaded cached data for ${name}:`, college.academic_fit_analysis);
        } else {
          console.log(`⚠️ [AcademicsTab] No valid data for ${name}, will show generate button`);
        }
      });

      if (Object.keys(cached).length > 0) {
        setAcademicFitData({ academicFit: cached });
      }
    };

    loadCachedData();
  }, [colleges]);

  // Generate academic table data for a specific college
  const handleGenerateAcademicData = async (college) => {
    setGeneratingDataForCollege(college.id);
    try {
      await generateBasicData(college, () => {
        if (onDataRefresh) onDataRefresh();
      });
    } finally {
      setGeneratingDataForCollege(null);
    }
  };

  // Generate academic data for ALL colleges at once
  const handleGenerateAllAcademicData = async () => {
    setGeneratingAnalysis(true);
    const collegesNeedingData = colleges.filter(c => !hasAcademicData(c));

    if (collegesNeedingData.length === 0) {
      toast.success('All colleges already have academic data!');
      setGeneratingAnalysis(false);
      return;
    }

    const toastId = toast.loading(`Generating academic data for ${collegesNeedingData.length} college${collegesNeedingData.length > 1 ? 's' : ''}...`);

    try {
      for (const college of collegesNeedingData) {
        await generateBasicData(college);
      }
      toast.success('Academic data generated for all colleges!', { id: toastId });
      if (onDataRefresh) onDataRefresh();
    } catch (error) {
      toast.error('Failed to generate some academic data', { id: toastId });
    } finally {
      setGeneratingAnalysis(false);
    }
  };

  // Check if college has academic data for the table
  const hasAcademicData = (college) => {
    const gc = college.global_colleges;
    return gc?.student_teacher_ratio || gc?.research_opportunities || gc?.avg_class_size;
  };

  // Auto-generate academic data when tab loads if missing
  useEffect(() => {
    const collegesNeedingData = colleges.filter(c => !hasAcademicData(c));

    if (collegesNeedingData.length > 0 && !generatingAnalysis) {
      console.log(`🤖 Auto-generating academic data for ${collegesNeedingData.length} colleges`);
      handleGenerateAllAcademicData();
    }
  }, [colleges.length]); // Only run when colleges array changes

  // Generate Academic Fit Analysis
  const generateAcademicFitAnalysis = async () => {
    setGeneratingAnalysis(true);
    try {
      console.log('🎓 [AcademicsTab] Generating academic fit analysis for user:', currentUser?.id);
      console.log('🎓 [AcademicsTab] Colleges being sent:', colleges.map(c => ({
        journey_id: c.id,
        global_id: c.global_colleges?.id,
        name: c.global_colleges?.name || c.name
      })));

      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-academic-fit-analysis',
          colleges: colleges.map(c => ({
            journey_id: c.id,  // college_journey_colleges.id (for database updates)
            id: c.global_colleges?.id,  // global_colleges.id (for reference)
            name: c.global_colleges?.name || c.name,
            popular_majors: c.global_colleges?.popular_majors,
            special_programs: c.global_colleges?.special_programs,
            interdisciplinary_options: c.global_colleges?.interdisciplinary_options,
            study_abroad_programs: c.global_colleges?.study_abroad_programs,
            honors_programs: c.global_colleges?.honors_programs,
            student_faculty_ratio: c.global_colleges?.student_faculty_ratio,
            research_opportunities: c.global_colleges?.research_opportunities,
            ...c.global_colleges
          })),
          studentProfile,
          userId: currentUser?.id
        })
      });

      if (!response.ok) throw new Error('Failed to generate academic fit analysis');

      const { data } = await response.json();
      console.log('✅ [AcademicsTab] Received response:', data);

      if (data?.academicFit) {
        setAcademicFitData(data);
        console.log('✅ [AcademicsTab] Set academicFitData with', Object.keys(data.academicFit).length, 'colleges');
        toast.success('Academic fit analysis generated successfully');
      } else {
        console.warn('⚠️ [AcademicsTab] Response missing academicFit data:', data);
      }
    } catch (error) {
      console.error('Error generating academic fit analysis:', error);
      toast.error('Failed to generate academic fit analysis');
    } finally {
      setGeneratingAnalysis(false);
    }
  };

  // Render star rating
  const renderStars = (stars) => {
    // Validate stars input
    if (!stars || typeof stars !== 'number' || isNaN(stars) || stars < 0) {
      return <span className="text-sm text-gray-600">N/A</span>;
    }

    // Clamp stars between 0 and 5
    const clampedStars = Math.min(Math.max(stars, 0), 5);
    const fullStars = Math.floor(clampedStars);
    const hasHalfStar = clampedStars % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center gap-0.5">
        {[...Array(fullStars)].map((_, i) => (
          <Star key={`full-${i}`} className="w-4 h-4 fill-current text-teal-500" />
        ))}
        {hasHalfStar && (
          <Star className="w-4 h-4 text-teal-500" style={{ fillOpacity: 0.5 }} />
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={`empty-${i}`} className="w-4 h-4 text-gray-300" />
        ))}
        <span className="text-sm text-gray-600 ml-1">({clampedStars}/5)</span>
      </div>
    );
  };

  // Format student ratio
  const formatRatio = (ratio) => {
    if (!ratio) return 'N/A';
    return `${ratio}:1`;
  };

  // Academic Overview Metrics
  const academicMetrics = [
    {
      label: 'Student:Teacher Ratio',
      getValue: (college) => formatRatio(college.global_colleges?.student_teacher_ratio)
    },
    {
      label: 'Popular Majors',
      field: 'global_colleges.popular_majors'
    },
    {
      label: 'Major Declaration',
      getValue: () => 'N/A'
    },
    {
      label: 'Research Opportunities',
      getValue: (college) => college.global_colleges?.research_opportunities || 'N/A'
    },
    {
      label: 'Internship Opportunities',
      getValue: (college) => college.global_colleges?.internship_opportunities || 'N/A'
    },
    {
      label: 'Average Class Size',
      getValue: (college) => college.global_colleges?.avg_class_size
        ? `${college.global_colleges.avg_class_size} students`
        : 'N/A'
    },
    {
      label: 'Study Abroad',
      getValue: (college) => college.global_colleges?.study_abroad || 'N/A'
    },
    {
      label: 'Honors Programs',
      getValue: (college) => college.honors_program_info ? 'Yes' : 'N/A'
    }
  ];

  // Program Rankings Metrics
  const rankingMetrics = [
    {
      label: 'Computer Science',
      getValue: (college) => college.global_colleges?.cs_ranking
        ? `#${college.global_colleges.cs_ranking}`
        : '—'
    },
    {
      label: 'Engineering',
      getValue: (college) => college.global_colleges?.engineering_ranking
        ? `#${college.global_colleges.engineering_ranking}`
        : '—'
    },
    {
      label: 'Business',
      getValue: (college) => college.global_colleges?.business_ranking
        ? `#${college.global_colleges.business_ranking}`
        : '—'
    },
    {
      label: 'Nursing',
      getValue: (college) => college.global_colleges?.nursing_ranking
        ? `#${college.global_colleges.nursing_ranking}`
        : '—'
    }
  ];

  // Create AI update handlers for Academic Overview table - these generate table data
  const academicTableHandlers = {};
  colleges.forEach(college => {
    if (!hasAcademicData(college)) {
      academicTableHandlers[college.id] = {
        tooltip: 'Generate academic data with AI',
        onClick: () => handleGenerateAcademicData(college),
        isLoading: generatingDataForCollege === college.id
      };
    }
  });

  // Create AI update handlers for Program Rankings table - these also generate table data
  const rankingTableHandlers = {};
  colleges.forEach(college => {
    const hasRankings = college.global_colleges?.cs_ranking ||
                       college.global_colleges?.engineering_ranking ||
                       college.global_colleges?.business_ranking;
    if (!hasRankings) {
      rankingTableHandlers[college.id] = {
        tooltip: 'Generate ranking data with AI',
        onClick: () => handleGenerateAcademicData(college),
        isLoading: generatingDataForCollege === college.id
      };
    }
  });

  // Check if any college is missing academic data
  const anyMissingData = colleges.some(c => !hasAcademicData(c));

  return (
    <div className="space-y-8">
      {/* Academic Stats Grid */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">Academic Overview</h2>
          {anyMissingData && (
            <button
              onClick={handleGenerateAllAcademicData}
              disabled={generatingAnalysis}
              className="px-4 py-2 bg-blue-500/90 text-white text-sm font-medium rounded-lg border-2 border-slate-900 hover:bg-blue-700 disabled:bg-gray-400 transition-all active:scale-95 flex items-center gap-2"
            >
              {generatingAnalysis ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generating All Data...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate All Academic Data
                </>
              )}
            </button>
          )}
        </div>
        <ComparisonTable
          colleges={colleges}
          metrics={academicMetrics}
          aiUpdateHandlers={academicTableHandlers}
        />
      </div>

      {/* Rankings Comparison */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-6">Program Rankings</h2>
        <ComparisonTable
          colleges={colleges}
          metrics={rankingMetrics}
          aiUpdateHandlers={rankingTableHandlers}
        />
        <p className="test-sm text-slate-500 mt-2 italic">— indicates ranking not available or not applicable</p>
      </div>

      {/* Academic Fit Analysis */}
      <div>
        {/* Always show Generate/Regenerate button at section level */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">Academic Fit Analysis</h2>
          <button
            onClick={generateAcademicFitAnalysis}
            disabled={generatingAnalysis}
            className="px-4 py-2 bg-teal-500/90 text-white font-medium rounded-lg border-2 border-slate-900 hover:bg-teal-700 disabled:bg-gray-400 transition-all active:scale-95 flex items-center gap-2"
          >
            {generatingAnalysis ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                {academicFitData?.academicFit ? 'Regenerating...' : 'Generating...'}
              </>
            ) : (
              <>
                {academicFitData?.academicFit ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                {academicFitData?.academicFit ? 'Regenerate' : 'Generate Analysis'}
              </>
            )}
          </button>
        </div>

        {academicFitData?.academicFit ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {colleges.map((college, idx) => {
              const collegeName = college.global_colleges?.name || college.name;
              const fitData = academicFitData.academicFit[collegeName];

              if (!fitData) return null;

              return (
                <SchoolCard
                  key={college.id}
                  college={college}
                  index={idx}
                  category={college.category}
                  showCategory={false}
                  onDelete={onDeleteCollege}
                >
                  <div className="space-y-4">
                    {/* Fit Score */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="test-sm font-semibold text-slate-600 uppercase">Academic Fit</span>
                        {renderStars(fitData.stars)}
                      </div>
                      <p className="text-xs font-semibold text-slate-900">{fitData.label}</p>
                    </div>

                    {/* Major & Program Fit */}
                    {fitData.majorProgramFit && fitData.majorProgramFit.length > 0 && (
                      <div>
                        <p className="test-sm font-semibold text-slate-600 uppercase mb-2">Program Strengths</p>
                        <ul className="space-y-1.5">
                          {fitData.majorProgramFit.map((point, i) => (
                            <li key={i} className="text-xs text-slate-700 leading-relaxed flex items-start gap-2">
                              <span className="text-teal-500 mt-1">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Cultural Match */}
                    {fitData.culturalMatch && fitData.culturalMatch.length > 0 && (
                      <div>
                        <p className="test-sm font-semibold text-slate-600 uppercase mb-2">Academic Culture</p>
                        <ul className="space-y-1.5">
                          {fitData.culturalMatch.map((point, i) => (
                            <li key={i} className="text-xs text-slate-700 leading-relaxed flex items-start gap-2">
                              <span className="text-teal-500 mt-1">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Distinctive Opportunities */}
                    {fitData.distinctiveOpportunities && fitData.distinctiveOpportunities.length > 0 && (
                      <div>
                        <p className="test-sm font-semibold text-slate-600 uppercase mb-2">Special Opportunities</p>
                        <ul className="space-y-1.5">
                          {fitData.distinctiveOpportunities.map((point, i) => (
                            <li key={i} className="text-xs text-slate-700 leading-relaxed flex items-start gap-2">
                              <span className="text-amber-500 mt-1">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Challenges */}
                    {fitData.challenges && fitData.challenges.length > 0 && (
                      <div>
                        <p className="test-sm font-semibold text-slate-600 uppercase mb-2">Considerations</p>
                        <ul className="space-y-1.5">
                          {fitData.challenges.map((point, i) => (
                            <li key={i} className="text-xs text-slate-600 leading-relaxed flex items-start gap-2">
                              <span className="text-slate-400 mt-1">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </SchoolCard>
              );
            })}
            </div>
          </>
        ) : (
          <div className="bg-white rounded-lg border-2 border-slate-300 p-8 text-center">
            <p className="text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
              Click "Generate Analysis" above to create personalized insights about major-specific programs,
              research opportunities, and academic culture at each school based on your academic profile.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademicsTab;
